import { authClient } from '@/auth';

/**
 * localStorage keys matching the auth client's BrowserStorage (src/lib/auth/client/token-storage.ts).
 *
 * FRAGILE: these are internal to the auth library. If it changes them, auth
 * injection silently breaks. Prefer `applySession()` once the auth library
 * exports it, and remove this fallback.
 */
const ACCESS_TOKEN_KEY = 'predictions:access_token';
const USER_PROFILE_KEY = 'predictions:user_profile';

interface AuthPayload {
  accessToken: string;
  profile: Record<string, unknown>;
}

declare global {
  interface Window {
    /** Set by the RN app's `injectJavaScript` with the base64-encoded auth payload. */
    __NATIVE_AUTH_TOKEN__?: string;
    /** Injected by React Native WebView runtime. */
    ReactNativeWebView?: {
      postMessage: (message: string) => void;
    };
  }
}

/** Whether the page is running inside a React Native WebView. */
export function isNativeWebView(): boolean {
  return typeof window.ReactNativeWebView?.postMessage === 'function';
}

/**
 * Asks the RN app to open the system browser for Google sign-in.
 *
 * Called from the Google sign-in button handlers (`AuthLoginForm`, `SignupStepMethod`)
 * when the page is inside a WebView. Firebase's `signInWithPopup` fails in WebViews
 * (Google blocks OAuth in embedded browsers), so we delegate to the system browser
 * via the RN app instead.
 *
 * The RN app receives this message in its `onMessage` handler and should:
 * 1. Open the system browser to `/auth/native-google-signin`
 * 2. Listen for the `crownprediction://auth?token=<base64>` deep link
 * 3. Decode the base64 payload and inject via `injectJavaScript`
 */
export function requestNativeGoogleSignIn(): void {
  window.ReactNativeWebView?.postMessage(JSON.stringify({ type: 'google-signin' }));
}

/**
 * Asks the RN app to open the system browser for Apple sign-in.
 *
 * Same pattern as {@link requestNativeGoogleSignIn}: Firebase's `signInWithPopup`
 * fails inside WebViews, so we delegate to the system browser via the RN app.
 */
export function requestNativeAppleSignIn(): void {
  window.ReactNativeWebView?.postMessage(JSON.stringify({ type: 'apple-signin' }));
}

/**
 * Asks the RN app to open the system browser for Facebook sign-in.
 *
 * Same pattern as {@link requestNativeGoogleSignIn}: Firebase's `signInWithPopup`
 * fails inside WebViews, so we delegate to the system browser via the RN app.
 */
export function requestNativeFacebookSignIn(): void {
  window.ReactNativeWebView?.postMessage(JSON.stringify({ type: 'facebook-signin' }));
}

/**
 * Applies auth credentials received from the RN app.
 *
 * The `token` is the base64-encoded JSON `{ accessToken, profile }` — the same
 * value the system browser puts on the `crownprediction://auth?token=` deep link.
 */
function applyNativeAuth(token: string): void {
  let payload: AuthPayload;
  try {
    const bytes = Uint8Array.from(atob(token), (c) => c.charCodeAt(0));
    payload = JSON.parse(new TextDecoder().decode(bytes)) as AuthPayload;
  } catch {
    console.error('[native-bridge] Failed to decode auth payload');
    return;
  }

  const { accessToken, profile } = payload;

  // Use applySession() if the auth library supports it (added in a future release).
  const client = authClient as unknown as { applySession?: (t: string, p: unknown) => void };
  if (typeof client.applySession === 'function') {
    client.applySession(accessToken, profile);
    return;
  }

  // Fallback: write to the same localStorage keys the auth library uses,
  // then reload. On reload, restoreSession() finds the stored token +
  // profile and sets isAuthenticated: true.
  localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
  localStorage.setItem(USER_PROFILE_KEY, JSON.stringify(profile));
  window.location.reload();
}

/**
 * Registers a listener for the RN app's auth token injection.
 *
 * The RN app injects credentials via:
 * ```js
 * webViewRef.injectJavaScript(`
 *   (function() {
 *     window.__NATIVE_AUTH_TOKEN__ = ${JSON.stringify(token)};
 *     window.dispatchEvent(new CustomEvent('nativeAuthToken', { detail: { token: ${JSON.stringify(token)} } }));
 *   })();
 *   true;
 * `);
 * ```
 *
 * Called once at startup in `main.tsx`. Harmless in a regular browser.
 */
export function initNativeBridge(): void {
  window.addEventListener('nativeAuthToken', ((e: CustomEvent<{ token: string }>) => {
    applyNativeAuth(e.detail.token);
  }) as EventListener);

  // Also check if the token was set before the listener was registered.
  if (window.__NATIVE_AUTH_TOKEN__) {
    applyNativeAuth(window.__NATIVE_AUTH_TOKEN__);
  }
}
